import { apiClient } from 'src/api/apiClient';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  DELETE_REALTIME_ICP,
  GET_ICP_SETUP_STATUS,
  GET_REALTIME_ICP_CONFIG,
  SAVE_REALTIME_ICP_CONFIG,
  ICP_TRAIN_TRIGGER,
  GET_EMP_BY_DEPT_VALUES,
  GET_ICP_INTENT_TOPICS,
} from 'src/constants/httpEndPoints';
import { RealTimeICPConfigData, RealTimeICPConfigDataSavePayload, RealTimeICPGetPayload, RealTimeICPStatus } from './types';
const getJsonValue = (input: string | null) => {
  if(!input) return [];
  try {
    return JSON.parse(input);
  }catch(e) {
    return [];
  }
}
export const getRealTimeICPConfig = createAsyncThunk<Record<string, RealTimeICPConfigData[]>, RealTimeICPGetPayload>(
  'icp-scoring/getRealTimeICPConfig',
  async (payload) => {
    const { emailId, customerId } = payload;

    const res = await apiClient.get(
      GET_REALTIME_ICP_CONFIG,
      {
        headers: {
          'X-Customer-Id': customerId,
          'X-Login-Email': emailId,
        },
      },
    );
    return {
      'standard' : res.data?.["standard"]?.map((item: any)=> {
        return {
          ...item,
          jsonValues: getJsonValue(item.jsonValues),
        }
      }) || [],
      'advanced' : res.data?.["advanced"]?.map((item: any)=> {
        return {
          ...item,
          jsonValues: getJsonValue(item.jsonValues),
        }
      }) || [],
      'aiSignals' : res.data?.["aiSignals"]?.map((item: any)=> {
        return {
          ...item,
          jsonValues: getJsonValue(item.jsonValues),
        }
      }) || [],
    }
  },
);

export const saveRealTimeICPConfig = createAsyncThunk<void, RealTimeICPConfigDataSavePayload>(
  'icp-scoring/saveRealTimeICPConfig',
  async (payload) => {
    const { emailId, customerId, body } = payload;
    await apiClient.post(
      SAVE_REALTIME_ICP_CONFIG,
      body,
      {
        headers: {
          'X-Customer-Id': customerId,
          'X-Login-Email': emailId,
        },
      },
    );
  },
);

export const getEmpByDeptValues = createAsyncThunk<string[], RealTimeICPGetPayload>(
  'icp-scoring/getEmpByDeptValues',
  async (payload) => {
    const { emailId, customerId } = payload;

    const res = await apiClient.get(
      GET_EMP_BY_DEPT_VALUES,
      {
        headers: {
          'X-Customer-Id': customerId,
          'X-Login-Email': emailId,
        },
      },
    );
    return res.data.list.map((item: any) => item.name);
  },
);

export const getIntentTopics = createAsyncThunk<string[], RealTimeICPGetPayload>(
  'icp-scoring/getIntentTopics',
  async (payload) => {
    const { emailId, customerId } = payload;

    const res = await apiClient.get(
      `${GET_ICP_INTENT_TOPICS}/${customerId}/getIntentTopic?page_size=25&searchText=`,
      {
        headers: {
          'X-Customer-Id': customerId,
          'X-Login-Email': emailId,
        },
      },
    );
    return res?.data?.list ?? [];
  },
);

export const getICPSetUpStatus = createAsyncThunk<RealTimeICPStatus, RealTimeICPGetPayload>(
  'icp-scoring/getICPSetUpStatus',
  async (payload) => {
    const { emailId, customerId } = payload;
    const res = await apiClient.get(
      `${GET_ICP_SETUP_STATUS}`,
      {
        headers: {
          'X-Customer-Id': customerId,
          'X-Login-Email': emailId,
        },
      },
    );
    return res.data;
  },
)

export const deleteRealTimeICP = createAsyncThunk<
  void,
  RealTimeICPGetPayload
>(
  'icp-scoring/deleteRealTimeICP',
  async (payload, { rejectWithValue }) => {
    const { emailId, customerId } = payload;
    try {
      await apiClient.post(
        `${DELETE_REALTIME_ICP}?uniqueId=${customerId}`,
        {},
        {
          headers: {
            'X-Customer-Id': customerId,
            'X-Login-Email': emailId,
          },
        },
      );
    } catch (e) {
      return rejectWithValue(e);
    }
  },
);

export const generateICPModel = createAsyncThunk(
  'icp/generateICPModel',
  async ({ customerId, emailId, setGenerateICPLoading, fetchICPTrainingDetails }:
    { customerId: string; emailId: string; setGenerateICPLoading: (loading: boolean) => void; fetchICPTrainingDetails: () => void },
    { rejectWithValue }
  ) => {
    setGenerateICPLoading(true);
    const authCheck = process.env.REACT_APP_DEV_ENV === 'prod';
    const triggerUrl = authCheck
      ? `${ICP_TRAIN_TRIGGER}?X-Login-Email=${emailId}`
      : ICP_TRAIN_TRIGGER;
    const payload = {
      customer_id: customerId,
      icp_trigger_type: 'TRAIN',
      skip_enrichment: true,
    };

    try {
      const response = await apiClient
        .put(triggerUrl, payload, {
          headers: {
            customer_id: customerId,
            ...(authCheck && {
              'X-Login-Email': emailId,
            }),
          },
        });

      if (response.data?.success) {
        fetchICPTrainingDetails();
        return response.data;
      }
    } catch (err) {
      console.log('Error is ', err);
      return rejectWithValue(err);
    } finally {
      setGenerateICPLoading(false);
    }
  }
)
